import { PathsV1OccupationSearchPostParametersQuerySelect } from "@/api-schema-enums";
import { useOccupationByUuidQuery } from "@/common/api/occupation/use-occupation-by-uuid.query";
import { useOccupationCreateMutation } from "@/common/api/occupation/use-occupation-create.mutation";
import { useOccupationSearchQuery } from "@/common/api/occupation/use-occupation-search.query";
import { useOccupationUpdateMutation } from "@/common/api/occupation/use-occupation-update.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormCardActions } from "@/components/ui/form-card";
import { Input } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Skeleton } from "@/components/ui/skeleton";
import { SystemInfoFields } from "@/components/system-info-fields/system-info-fields";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDebounce } from "@uidotdev/usehooks";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { z } from "zod";

export const OccupationFormSchema = (t: (key: string) => string) =>
  z.object({
    name: z.string().min(1, t("masterdata:NameIsRequired")).max(255, t("masterdata:OccupationNameMaxLength")),
    v1Id: z
      .string()
      .max(10, t("masterdata:V1IdMaxDigits"))
      .optional()
      .refine((val) => !val || /^\d+$/.test(val), {
        message: t("masterdata:V1IdMustBeValidPositiveNumber"),
      })
      .refine((val) => !val || parseInt(val, 10) <= 2147483647, {
        message: t("masterdata:V1IdMaxValue"),
      }),
  });

export type OccupationFormSchemaType = z.infer<ReturnType<typeof OccupationFormSchema>>;

interface OccupationFormFieldsProps {
  uuid?: string;
}

export function OccupationFormFields({ uuid }: OccupationFormFieldsProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const [_, navigate] = useLocation();
  const isUpdateMode = !!uuid;
  const [isSaving, setIsSaving] = useState(false);

  const { data: occupation, isPending: isOccupationLoading } = useOccupationByUuidQuery(uuid || "");

  const { mutateAsync: createMutation, isPending: isCreating } = useOccupationCreateMutation();
  const { mutateAsync: updateMutation, isPending: isUpdating } = useOccupationUpdateMutation();

  const form = useForm<OccupationFormSchemaType>({
    resolver: zodResolver(OccupationFormSchema(t)),
    defaultValues: {
      name: "",
      v1Id: "",
    },
  });

  useEffect(() => {
    if (isUpdateMode && occupation) {
      form.reset({
        name: occupation.name || "",
        v1Id: occupation.v1Id ? String(occupation.v1Id) : "",
      });
    }
  }, [occupation, form, isUpdateMode]);

  const [watchedName, watchedV1Id] = useWatch({ control: form.control, name: ["name", "v1Id"] });

  const debouncedName = useDebounce(watchedName, 500);
  const debouncedV1Id = useDebounce(watchedV1Id, 500);

  const { data: nameCheck, isFetching: isCheckingName } = useOccupationSearchQuery({
    options: {
      body: { name: [debouncedName] },
      parameters: {
        pageSize: 1,
        select: [
          PathsV1OccupationSearchPostParametersQuerySelect.Uuid,
          PathsV1OccupationSearchPostParametersQuerySelect.Name,
        ],
      },
    },
    enabled: !!debouncedName && debouncedName.trim().length > 0 && !isSaving,
  });

  const { data: v1IdCheck, isFetching: isCheckingV1Id } = useOccupationSearchQuery({
    options: {
      body: { v1Id: debouncedV1Id ? [Number(debouncedV1Id)] : undefined },
      parameters: {
        pageSize: 1,
        select: [PathsV1OccupationSearchPostParametersQuerySelect.Uuid],
      },
    },
    enabled: !!debouncedV1Id && debouncedV1Id.trim().length > 0 && !Number.isNaN(Number(debouncedV1Id)) && !isSaving,
  });

  const isNameDuplicate = useMemo(() => {
    if (nameCheck?.data && nameCheck.data.length > 0) {
      const duplicate = nameCheck.data[0];
      return !isUpdateMode || duplicate.uuid !== uuid;
    }
    return false;
  }, [nameCheck, isUpdateMode, uuid]);

  const isV1IdDuplicate = useMemo(() => {
    if (v1IdCheck?.data && v1IdCheck.data.length > 0) {
      const duplicate = v1IdCheck.data[0];
      return !isUpdateMode || duplicate.uuid !== uuid;
    }
    return false;
  }, [v1IdCheck, isUpdateMode, uuid]);

  const duplicateErrors = useMemo(() => {
    const errors: string[] = [];

    if (isNameDuplicate) {
      errors.push(t("masterdata:OccupationNameAlreadyExists", { name: debouncedName }));
    }

    if (isV1IdDuplicate) {
      errors.push(t("masterdata:OccupationV1IdAlreadyExists", { v1Id: debouncedV1Id }));
    }

    return errors;
  }, [isNameDuplicate, isV1IdDuplicate, debouncedName, debouncedV1Id, t]);

  const onSubmit = async (values: OccupationFormSchemaType): Promise<void> => {
    setIsSaving(true);

    try {
      if (isUpdateMode && uuid) {
        await toastMutationStatus({
          mutationFn: () =>
            updateMutation({
              uuid,
              name: values.name,
              v1Id: values.v1Id?.trim() ? Number(values.v1Id) : null,
            }),
          successMessage: t("masterdata:OccupationSuccessfullyUpdated"),
          errorMessage: t("masterdata:ErrorUpdatingOccupation"),
          onSuccess: () => {
            navigate("~/masterdata/occupation");
          },
        });
      } else {
        await toastMutationStatus({
          mutationFn: () =>
            createMutation({
              name: values.name,
              v1Id: values.v1Id?.trim() ? Number(values.v1Id) : null,
            }),
          successMessage: t("masterdata:OccupationSuccessfullyCreated"),
          errorMessage: t("masterdata:ErrorCreatingOccupation"),
          onSuccess: () => {
            navigate("~/masterdata/occupation");
          },
        });
      }
    } catch (error) {
      console.error(`Error ${isUpdateMode ? "updating" : "creating"} occupation:`, error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmitWithValidation = async (values: OccupationFormSchemaType): Promise<void> => {
    if (isPending || isCheckingDuplicates) {
      return;
    }

    if (duplicateErrors.length > 0) {
      for (const error of duplicateErrors) {
        toast.error(t("common:ValidationError"), {
          description: error,
          duration: 5000,
        });
      }
      return;
    }
    await onSubmit(values);
  };

  const isPending = isCreating || isUpdating;
  const isLoading = isUpdateMode && isOccupationLoading;
  const isCheckingDuplicates = isCheckingName || isCheckingV1Id;

  if (isLoading) {
    return (
      <div className="space-y-2">
        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        <div className="space-y-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>

        <div className="flex justify-end space-x-4">
          <Skeleton className="h-10 w-24 rounded-md" />
          <Skeleton className="h-10 w-24 rounded-md" />
        </div>
      </div>
    );
  }

  const isSubmitDisabled = isPending || duplicateErrors.length > 0 || !form.formState.isValid || isCheckingDuplicates;

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmitWithValidation)}>
        <Card>
          <CardContent className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common:Name")} *</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder={t("masterdata:OccupationNamePlaceholder")}
                        {...field}
                        error={isNameDuplicate}
                      />
                      {isCheckingName && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  <div className="flex min-h-5 items-center">
                    {!isCheckingName && isNameDuplicate && (
                      <p className="text-error text-sm">{t("masterdata:OccupationNameDuplicateError")}</p>
                    )}
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="v1Id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("masterdata:V1ID")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input placeholder={t("masterdata:V1IdExample")} {...field} error={isV1IdDuplicate} />
                      {isCheckingV1Id && (
                        <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
                        </div>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                  <div className="flex min-h-5 items-center">
                    {!isCheckingV1Id && isV1IdDuplicate && (
                      <p className="text-error text-sm">{t("masterdata:OccupationV1IdDuplicateError")}</p>
                    )}
                  </div>
                </FormItem>
              )}
            />

            {isUpdateMode && (
              <SystemInfoFields
                uuid={occupation?.uuid}
                createdAt={occupation?.createdAt}
                updatedAt={occupation?.updatedAt}
              />
            )}
          </CardContent>
        </Card>

        <FormCardActions
          cancelLabel={t("common:Cancel")}
          onCancel={() => navigate("~/masterdata/occupation")}
          isPending={isPending}
          submitDisabled={isSubmitDisabled}
          submitLabel={t("common:Save")}
        />
      </form>
    </Form>
  );
}
